import { useEffect, useRef, useState } from 'react';
import {
  addReorderItem,
  cleanPreviewTitle,
  findExistingItem,
  looksLikeUrl,
  matchVendorName,
} from '../../lib/reorder';
import { useAuth } from '../../context/AuthContext';
import VendorAutocomplete from '../VendorAutocomplete';
import { fetchPreview } from '../../lib/linkPreview';
import { toHttpUrl, prettyUrl } from '../../lib/url';
import LinkThumbnail from './LinkThumbnail';

const BLANK = { item_name: '', vendor: '', quantity: '', needed_by: '', link_url: '' };

// Adding something to the To Order list.
//
// This was a five-field row — name, qty, vendor, needed-by, link — every one
// of them on screen for every add, when the usual case is "I'm out of this"
// and the usual source is a product page already open in another tab. So:
//
//   - One box, which takes either a link or a name.
//   - A link is *read*: the page's og:title, og:site_name and og:image become
//     the item name, the supplier and the thumbnail, and you confirm rather
//     than transcribe. Same Edge Function (and cache) the list already uses
//     for thumbnails, so a link pasted twice costs nothing the second time.
//   - A plain name with nothing else to decide is added on Enter, no card.
//
// The confirm card only appears when there's something to confirm: a fetched
// guess to check, or an item that looks like one already on the list.
export default function QuickAddItem({ items, vendors, onCreateVendor, onChanged }) {
  const { user } = useAuth();
  const [capture, setCapture] = useState('');
  const [draft, setDraft] = useState(null); // null = the one-box state
  const [preview, setPreview] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const nameRef = useRef(null);
  // Only the newest paste may write into the draft: pasting a second link
  // before the first resolves would otherwise fill the card from whichever
  // shop's server happened to answer last.
  const latest = useRef(0);
  const focused = useRef(false);

  // Whatever the draft is for, the name is the one field that has to be right,
  // so put the cursor there once the guess has landed.
  //
  // Once per draft, not once per render: `draft` is a new object on every
  // keystroke, so refocusing unconditionally would snatch the cursor out of
  // Qty and back into the name field with every character typed.
  useEffect(() => {
    if (!draft) {
      focused.current = false;
      return;
    }
    if (resolving || focused.current) return;
    focused.current = true;
    nameRef.current?.focus();
  }, [draft, resolving]);

  const reset = () => {
    latest.current += 1;
    setDraft(null);
    setPreview(null);
    setResolving(false);
    setNote(null);
    setCapture('');
  };

  const setField = (key) => (value) => setDraft((d) => (d ? { ...d, [key]: value } : d));

  const save = async (fields) => {
    if (!fields.item_name.trim()) {
      setError('Give the item a name first — everything else is optional.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // A vendor name typed but never picked from the dropdown still has to
      // become a vendor, or the supplier silently vanishes on save — the same
      // resolution the expense and product forms do.
      const typed = fields.vendor.trim();
      let vendorId = null;
      if (typed) {
        const match = vendors.find((v) => v.name.toLowerCase() === typed.toLowerCase());
        vendorId = match ? match.id : (await onCreateVendor(typed)).id;
      }

      await addReorderItem({
        item_name: fields.item_name.trim(),
        vendor_id: vendorId,
        quantity: fields.quantity.trim() || null,
        needed_by: fields.needed_by || null,
        link_url: toHttpUrl(fields.link_url),
        created_by: user.id,
      });
      reset();
      await onChanged();
    } catch (err) {
      // A failed insert used to vanish silently, leaving the row unsaved with
      // nothing on screen to explain it.
      setError(`Couldn't add that: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const readLink = async (href) => {
    const token = (latest.current += 1);
    setResolving(true);
    setNote(null);
    try {
      const row = await fetchPreview(href);
      if (latest.current !== token) return;
      setPreview(row);

      const guessed = cleanPreviewTitle(row?.title, row?.site_name);
      if (guessed) {
        setDraft((d) =>
          d ? { ...d, item_name: d.item_name || guessed } : d
        );
      } else {
        setNote("That page didn't say what it's selling — name it yourself.");
      }

      const vendor = matchVendorName(vendors, row?.site_name, href);
      if (vendor) setDraft((d) => (d ? { ...d, vendor: d.vendor || vendor } : d));
    } catch {
      if (latest.current !== token) return;
      setNote("Couldn't read that page — name the item yourself, the link is saved either way.");
    } finally {
      if (latest.current === token) setResolving(false);
    }
  };

  const handleCapture = (e) => {
    e.preventDefault();
    const typed = capture.trim();
    if (!typed) {
      setError('Paste a product link, or type what you need.');
      return;
    }
    setError(null);

    if (looksLikeUrl(typed)) {
      const href = toHttpUrl(typed);
      setDraft({ ...BLANK, link_url: href || typed });
      setPreview(null);
      readLink(href);
      return;
    }

    // A name with nothing to check goes straight on the list. If it looks like
    // something already there, stop and show it rather than quietly making a
    // second copy — which is exactly how this list grew twins before.
    const clash = findExistingItem(items, { name: typed });
    if (clash) {
      setDraft({ ...BLANK, item_name: typed });
      return;
    }
    save({ ...BLANK, item_name: typed });
  };

  if (!draft) {
    return (
      <div className="toorder-add">
        <form className="toorder-capture" onSubmit={handleCapture}>
          <input
            className="toorder-capture-input"
            placeholder="Paste a product link, or type what you need"
            value={capture}
            onChange={(e) => setCapture(e.target.value)}
            aria-label="Add an item to order"
          />
          {/* Deliberately not disabled when empty: a disabled submit also
              blocks the form's implicit Enter, so pressing Add did nothing at
              all and explained nothing. It always submits and says what's
              missing. */}
          <button type="submit" className="primary-action" disabled={saving}>
            {saving ? 'Adding…' : 'Add'}
          </button>
        </form>
        <p className="toorder-capture-hint">
          A product link fills in the name, supplier and photo for you.
        </p>
        {error && <p className="po-error">{error}</p>}
      </div>
    );
  }

  const clash = findExistingItem(items, { link: draft.link_url, name: draft.item_name });
  const href = toHttpUrl(draft.link_url);

  return (
    <div className="toorder-add">
      <form
        className="toorder-draft"
        onSubmit={(e) => {
          e.preventDefault();
          save(draft);
        }}
      >
        <div className="toorder-draft-head">
          {draft.link_url && (
            <LinkThumbnail
              href={href}
              // A missing preview means "still looking" to LinkThumbnail, so
              // once the fetch is over it must be given *something* or the
              // tile spins forever on a page with no og:image.
              preview={resolving ? null : preview || { status: 'error' }}
              alt={draft.item_name}
            />
          )}
          <div className="toorder-draft-identity">
            <input
              ref={nameRef}
              className="toorder-draft-name"
              placeholder={resolving ? 'Reading the page…' : 'What is it?'}
              value={draft.item_name}
              onChange={(e) => setField('item_name')(e.target.value)}
              aria-label="Item name"
            />
            {href && (
              <a
                className="toorder-item-link"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {prettyUrl(draft.link_url)}
              </a>
            )}
          </div>
        </div>

        <div className="toorder-draft-fields">
          <label className="toorder-draft-field">
            <span>Supplier</span>
            <VendorAutocomplete
              value={draft.vendor}
              onChange={setField('vendor')}
              vendors={vendors}
              onCreateVendor={onCreateVendor}
              placeholder={resolving ? '…' : 'Optional'}
            />
          </label>
          <label className="toorder-draft-field">
            <span>Qty</span>
            <input
              value={draft.quantity}
              onChange={(e) => setField('quantity')(e.target.value)}
              placeholder="2 kg, 1 tray…"
            />
          </label>
          <label className="toorder-draft-field">
            <span>Needed by</span>
            <input
              type="date"
              value={draft.needed_by}
              onChange={(e) => setField('needed_by')(e.target.value)}
            />
          </label>
        </div>

        {note && <p className="toorder-draft-note">{note}</p>}

        {clash && (
          <p className="toorder-draft-warning">
            <strong>{clash.item_name}</strong> is already on your list
            {clash.status === 'Ordered' ? ' and on order' : ''}
            {clash.created_at
              ? ` — added ${new Date(clash.created_at).toLocaleDateString()}`
              : ''}
            .
          </p>
        )}

        {error && <p className="po-error">{error}</p>}

        <div className="toorder-draft-actions">
          <button type="button" className="ghost-button" onClick={reset} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="primary-action" disabled={saving || resolving}>
            {saving ? 'Adding…' : clash ? 'Add anyway' : 'Add item'}
          </button>
        </div>
      </form>
    </div>
  );
}
